import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { DecisionDialog } from "../../components/portal/DecisionDialog";
import { PortalPage, EmptyState, Pill, Section } from "../../components/portal/PortalPage";
import { usePortal } from "../../components/portal/usePortal";
import { fmtDate, fmtMoney, QUOTE_STATUS, REQUEST_STATUS } from "../../components/portal/portalUtils";
import { downloadAuthFile } from "../../lib/fileOpen";
import { Receipt, DownloadSimple, ClipboardText, CaretDown, CaretUp } from "@phosphor-icons/react";
import { CardListSkeleton } from "../../components/Skeletons";
import { toast } from "sonner";
import { ForClientChip, FamilyFilter, useFamilyFilter } from "../../components/portal/FamilyFilter";
import { QuoteNarrative } from "../../components/portal/QuoteNarrative";

export default function ClientQuotesPage() {
  const { perms } = usePortal();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [decision, setDecision] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => API.get("/client-hub/quotes").then(({ data }) => setData(data)).catch(() => setData({ quotes: [], request_quotes: [] })), []);
  useEffect(() => { load(); }, [load]);

  const respond = async ({ notes, po_number }) => {
    setBusy(true);
    try {
      await API.post(`/client-hub/quotes/${decision.id}/respond`, { decision: decision.kind, notes, po_number });
      toast.success(decision.kind === "approve" ? "Quote approved" : "Quote declined");
      setDecision(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  const fam = useFamilyFilter();
  const pending = (data?.quotes || []).filter((q) => q.status === "sent" && fam.matches(q));
  const others = (data?.quotes || []).filter((q) => q.status !== "sent" && fam.matches(q));
  const reqPending = (data?.request_quotes || []).filter((r) => r.status === "submitted_to_client" && fam.matches(r));
  const reqOthers = (data?.request_quotes || []).filter((r) => r.status !== "submitted_to_client" && fam.matches(r));

  const QuoteCard = ({ q }) => {
    const expanded = open === q.id;
    return (
      <div className={`border rounded-lg bg-card p-4 ${q.status === "sent" ? "border-purple-300 dark:border-purple-800" : "border-border"}`} data-testid={`quote-card-${q.id}`}>
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1"><span className="text-[10px] font-mono text-muted-foreground">{q.reference}</span><Pill map={QUOTE_STATUS} value={q.status} /><ForClientChip name={q.for_client} clientId={q.client_id} /></div>
            <p className="text-sm font-semibold truncate">{q.title}</p>
            <p className="text-[11px] text-muted-foreground truncate">{q.address || "—"} · {fmtDate(q.sent_at || q.created_at)}{q.validity_days ? ` · valid ${q.validity_days} days` : ""}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xl font-bold font-[Chivo]">{fmtMoney(q.total_inc_vat || q.grand_total)}</p>
            <p className="text-[10px] text-muted-foreground">{q.total_inc_vat ? "inc VAT" : "ex VAT"}</p>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <Button size="sm" variant="ghost" onClick={() => setOpen(expanded ? null : q.id)} data-testid={`quote-toggle-${q.id}`}>{expanded ? <CaretUp size={14} /> : <CaretDown size={14} />} Items</Button>
            <Button size="sm" variant="outline" onClick={() => downloadAuthFile(`/api/client-hub/quotes/${q.id}/pdf`, `${q.reference}.pdf`)} data-testid={`quote-pdf-${q.id}`}><DownloadSimple size={14} /></Button>
            {q.status === "sent" && perms.can_approve && (
              <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setDecision({ id: q.id, kind: "approve", total: q.total_inc_vat || q.grand_total })} data-testid={`quote-approve-${q.id}`}>Approve</Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => setDecision({ id: q.id, kind: "decline" })} data-testid={`quote-decline-${q.id}`}>Decline</Button>
              </>
            )}
          </div>
        </div>
        {expanded && (
          <table className="w-full text-xs mt-4 border-t border-border" data-testid={`quote-items-${q.id}`}>
            <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground"><th className="text-left py-2">Item</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Total</th></tr></thead>
            <tbody className="divide-y divide-border">
              {q.line_items.map((li, i) => <tr key={i}><td className="py-2"><p className="font-medium">{li.item_name}</p>{li.description && <p className="text-muted-foreground">{li.description}</p>}</td><td className="py-2 text-right">{li.quantity}</td><td className="py-2 text-right font-medium">{fmtMoney(li.total)}</td></tr>)}
            </tbody>
            <tfoot><tr className="border-t border-border"><td colSpan={2} className="py-2 text-right text-muted-foreground">Subtotal</td><td className="py-2 text-right font-semibold">{fmtMoney(q.grand_total)}</td></tr>
              {q.vat_amount ? <tr><td colSpan={2} className="py-1 text-right text-muted-foreground">VAT</td><td className="py-1 text-right">{fmtMoney(q.vat_amount)}</td></tr> : null}
              {q.total_inc_vat ? <tr><td colSpan={2} className="py-1 text-right font-semibold">Total inc VAT</td><td className="py-1 text-right font-bold">{fmtMoney(q.total_inc_vat)}</td></tr> : null}</tfoot>
          </table>
        )}
        {expanded && <QuoteNarrative quote={q} showTerms className="mt-4" />}
        {q.client_response?.name && <p className="text-[10px] text-muted-foreground mt-2">{q.status === "declined" ? "Declined" : "Approved"} by {q.client_response.name} on {fmtDate(q.client_response.at)}{q.client_response.comment ? ` — "${q.client_response.comment}"` : ""}</p>}
      </div>
    );
  };

  const ReqQuote = ({ r }) => (
    <Link to={`/portal/requests/${r.jr_id}`} className={`block border rounded-lg bg-card p-4 card-lift ${r.status === "submitted_to_client" ? "border-purple-300 dark:border-purple-800" : "border-border"}`} data-testid={`request-quote-${r.jr_id}`}>
      <div className="flex items-center gap-3">
        <ClipboardText size={20} className="text-blue-500 shrink-0" weight="duotone" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5"><span className="text-[10px] font-mono text-muted-foreground">{r.reference}</span><Pill map={REQUEST_STATUS} value={r.status} /><ForClientChip name={r.for_client} clientId={r.client_id} /></div>
          <p className="text-sm font-semibold truncate">{r.title}</p>
          <p className="text-[11px] text-muted-foreground truncate">{r.address}{r.vendor_name ? ` · ${r.vendor_name}` : ""}</p>
        </div>
        <div className="text-right"><p className="text-lg font-bold font-[Chivo]">{fmtMoney(r.client_total)}</p><p className="text-[10px] text-muted-foreground">ex VAT</p></div>
      </div>
    </Link>
  );

  return (
    <PortalPage title="Quotes" subtitle="Everything awaiting your decision, plus your quote history." testid="client-quotes" actions={<FamilyFilter fam={fam} testid="quotes-client-filter" />}>
      {!data ? <CardListSkeleton rows={3} showHeader={false} /> : (
        <div className="space-y-6">
          <Section title={`Awaiting Your Decision (${pending.length + reqPending.length})`} testid="quotes-pending">
            {pending.length + reqPending.length === 0 ? <p className="text-xs text-muted-foreground">Nothing waiting on you right now.</p> : (
              <div className="space-y-3">{reqPending.map((r) => <ReqQuote key={r.jr_id} r={r} />)}{pending.map((q) => <QuoteCard key={q.id} q={q} />)}</div>
            )}
            {!perms.can_approve && pending.length + reqPending.length > 0 && <p className="text-[11px] text-muted-foreground mt-3">Only portal administrators can approve or decline quotes.</p>}
          </Section>
          <Section title="Quote History" testid="quotes-history">
            {others.length + reqOthers.length === 0 ? <EmptyState icon={Receipt} title="No past quotes" /> : (
              <div className="space-y-3">{reqOthers.map((r) => <ReqQuote key={r.jr_id} r={r} />)}{others.map((q) => <QuoteCard key={q.id} q={q} />)}</div>
            )}
          </Section>
        </div>
      )}
      <DecisionDialog decision={decision} onClose={() => setDecision(null)} onConfirm={respond} busy={busy} testPrefix="quote-decision" />
    </PortalPage>
  );
}
